/**
 * ModalShell — generic accessible modal.
 *
 * Consolidates the hand-rolled scrim/dialog/focus-trap pattern that lived in
 * RTM/ReassignModal, RPM/{KpiTileDetail, OverrideDrawer, QuickReassignModal,
 * BroadcastModal}, and similar.  Features:
 *
 *   - role="dialog" + aria-modal + aria-labelledby on the title.
 *   - Scrim click-to-close (role="presentation").
 *   - Focus trap — first focusable in modal on open, Tab cycles inside.
 *   - Escape closes via onClose().
 *   - Focus restoration — saves previously-focused element, restores on close.
 *   - Body scroll lock while open (document.body overflow: hidden).
 *   - Mounted via React Portal on document.body.
 *   - size: 'sm' | 'md' | 'lg' → controls max-width.
 */
import { useEffect, useId, useRef, useState, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from '@retail-ops/panel-shell/fi';

export type ModalShellSize = 'sm' | 'md' | 'lg' | 'xl' | 'auto';

export interface ModalShellProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: ModalShellSize;
  ariaLabel?: string;
  /** When true, scrim click no longer closes the modal. */
  disableBackdropClose?: boolean;
  /** When true, the Escape key no longer closes the modal. */
  disableEscapeClose?: boolean;
  /** When true, render no header bar (title and close X are hidden). */
  noHeader?: boolean;
  /**
   * Data-loss guard (Bill 2026-06-02). When `confirmClose` is set AND `isDirty`
   * is true, any close attempt (X / Escape / scrim) shows a positive
   * "discard?" confirm instead of closing immediately — so a stray click can
   * never silently destroy unsaved work. No-op when the modal isn't dirty.
   */
  confirmClose?: boolean;
  isDirty?: boolean;
  confirmCloseTitle?: string;
  confirmCloseMessage?: string;
}

const SIZE_WIDTHS: Record<ModalShellSize, string> = {
  sm: '420px',
  md: '560px',
  lg: '820px',
  xl: '1120px',
  auto: 'min(1100px, 95vw)',
};

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Primitive: ModalShell. */
export function ModalShell({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  ariaLabel,
  disableBackdropClose,
  disableEscapeClose,
  noHeader,
  confirmClose,
  isDirty,
  confirmCloseTitle = 'Discard unsaved changes?',
  confirmCloseMessage = 'Your work on this form will be lost if you leave now.',
}: ModalShellProps): JSX.Element | null {
  const modalRef = useRef<HTMLDivElement | null>(null);
  const priorFocus = useRef<HTMLElement | null>(null);
  const pressedOnScrim = useRef(false);
  const onCloseRef = useRef(onClose);
  const titleId = useId();

  // Data-loss guard state — when a close is requested on a dirty modal we hold
  // it here and render a positive discard/keep confirm rather than closing.
  const [pendingClose, setPendingClose] = useState(false);
  const confirmCloseRef = useRef(confirmClose);
  const isDirtyRef = useRef(isDirty);
  useEffect(() => { confirmCloseRef.current = confirmClose; isDirtyRef.current = isDirty; });

  // Keep onCloseRef current without retriggering the focus-init effect.
  // (Bill 2026-04-26: callers commonly pass an inline arrow as onClose,
  // which changed identity every render and refocused the modal — knocking
  // focus out of textareas on every keystroke.)
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    if (!open) return;

    // Save prior focus + lock body scroll.
    priorFocus.current = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Focus first focusable inside the modal.
    const first = modalRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    first?.focus();

    function onKeyDown(e: KeyboardEvent): void {
      // Keys pressed in the life ring (rendered on <body>, above this dialog - Bill 2026-10-16) belong to
      // the report form: Escape there must not close this dialog underneath.
      const t = e.target as Element | null;
      if (t && typeof t.closest === 'function' && t.closest('.support-overlay, .support-fab')) return;
      if (e.key === 'Escape') {
        if (disableEscapeClose) return;
        e.preventDefault();
        if (confirmCloseRef.current && isDirtyRef.current) setPendingClose(true);
        else onCloseRef.current();
        return;
      }
      if (e.key === 'Tab' && modalRef.current) {
        const focusables = modalRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
        if (focusables.length === 0) return;
        const firstEl = focusables[0];
        const lastEl = focusables[focusables.length - 1];
        if (!firstEl || !lastEl) return;
        if (e.shiftKey && document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        } else if (!e.shiftKey && document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    }

    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
      priorFocus.current?.focus();
    };
    // Only re-run when `open` toggles — onClose is read via ref above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, disableEscapeClose]);

  if (!open) return null;

  // Close request router — gate through the discard confirm when dirty.
  const requestClose = (): void => {
    if (confirmClose && isDirty) setPendingClose(true);
    else onClose();
  };

  // A scrim click counts only when the press also started on the scrim. A text selection dragged out of
  // the dialog and released on the scrim fires its click there, and closed Find Customer (CHG-0090).
  const scrimPress = (e: ReactMouseEvent): void => { pressedOnScrim.current = e.target === e.currentTarget; };
  const scrimClick = (e: ReactMouseEvent): boolean => {
    const ok = pressedOnScrim.current && e.target === e.currentTarget;
    pressedOnScrim.current = false;
    return ok;
  };

  const overlay = (
    <div
      role="presentation"
      // Phone (<= 640px): primitives.css turns the scrim + dialog into a full-screen sheet.
      className="modal-shell__scrim"
      onMouseDown={scrimPress}
      onClick={(e) => {
        if (!scrimClick(e) || disableBackdropClose) return;
        requestClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--overlay-bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: 'var(--sp-7)',
      }}
    >
      <div
        ref={modalRef}
        role="dialog"
        className="modal-shell__dialog"
        aria-modal="true"
        {...(ariaLabel
          ? { 'aria-label': ariaLabel }
          : { 'aria-labelledby': titleId })}
        style={{
          background: 'var(--surface-card)',
          borderRadius: 'var(--r-lg)',
          padding: 'var(--sp-8)',
          width: '100%',
          maxWidth: SIZE_WIDTHS[size],
          maxHeight: '90vh',
          // The BOX never scrolls — the body wrapper below does. When the
          // padded box was the scroll container, any sticky header inside the
          // body pinned a full padding-band below the modal's visual top and
          // scrolled content stayed visible through that band (Bill's
          // "content behind the frozen header" glitch, 2026-07-02). This also
          // keeps the title/X and footer actions always visible.
          overflow: 'hidden',
          boxShadow: 'var(--sh-4)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-6)',
        }}
      >
        {!noHeader && (
          <header
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 'var(--sp-5)',
            }}
          >
            <h2
              id={titleId}
              style={{
                margin: 0,
                fontSize: 16,
                color: 'var(--pcb-blue-dark)',
                fontWeight: 600,
              }}
            >
              {title}
            </h2>
            <button
              type="button"
              onClick={requestClose}
              aria-label="Close"
              className="modal-shell__close"
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--ink-600)',
                cursor: 'pointer',
                padding: 'var(--sp-3)',
                borderRadius: 'var(--r-sm)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <X size={16} aria-hidden />
            </button>
          </header>
        )}

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-5)',
            // The scroll region. minHeight 0 lets flexbox actually shrink it;
            // sticky elements inside pin flush to this wrapper's edges.
            overflowY: 'auto',
            minHeight: 0,
            flex: '1 1 auto',
          }}
        >
          {children}
        </div>

        {footer && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 'var(--sp-4)',
              paddingTop: 'var(--sp-5)',
              borderTop: '1px solid var(--ink-100)',
            }}
          >
            {footer}
          </div>
        )}
      </div>

      {pendingClose && (
        <div
          role="presentation"
          onMouseDown={scrimPress}
          onClick={(e) => { if (scrimClick(e)) setPendingClose(false); }}
          style={{
            position: 'fixed', inset: 0, background: 'var(--overlay-bg)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 110, padding: 'var(--sp-7)',
          }}
        >
          <div
            role="alertdialog"
            aria-modal="true"
            style={{
              background: 'var(--surface-card)', borderRadius: 'var(--r-lg)',
              padding: 'var(--sp-7)', maxWidth: 400, boxShadow: 'var(--sh-4)',
              display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)',
            }}
          >
            <h3 style={{ margin: 0, fontSize: 15, color: 'var(--pcb-blue-dark)', fontWeight: 600 }}>
              {confirmCloseTitle}
            </h3>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-600)' }}>{confirmCloseMessage}</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-4)' }}>
              <button
                type="button"
                onClick={() => setPendingClose(false)}
                style={{
                  padding: 'var(--sp-3) var(--sp-5)', borderRadius: 'var(--r-sm)',
                  border: '1px solid var(--ink-200)', background: 'transparent',
                  color: 'var(--ink-900)', cursor: 'pointer', fontSize: 13, fontWeight: 500,
                }}
              >
                Keep editing
              </button>
              <button
                type="button"
                onClick={() => { setPendingClose(false); onClose(); }}
                style={{
                  padding: 'var(--sp-3) var(--sp-5)', borderRadius: 'var(--r-sm)',
                  border: 'none', background: 'var(--err-fill)', color: 'var(--on-status-fill)',
                  cursor: 'pointer', fontSize: 13, fontWeight: 600,
                }}
              >
                Discard changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return createPortal(overlay, document.body);
}
